// ══════════════════════════════════════════════════════════════════════════════════════════════
//  ¿ES ADMINISTRADORA DE ESTA CLÍNICA? — LA PREGUNTA DEL NAVEGADOR, EN UN SOLO SITIO
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  LO QUE HABÍA. El barrido del servidor quitó las 32 copias de
//
//      const esAdmin = (req) => req.agente?.rol === 'admin' || req.agente?.es_superadmin === true;
//
//  y dejó LA MISMA FRASE, literal, en DOCE sitios del navegador: Layout, AdminLayout, AdminInicio,
//  Ajustes, Contabilidad, Derivaciones, Encuestas, AlertasEnvios, SedacionCita, useCorreoClinica y
//  AuthContext. MEDIDO por HTTP con dos cuentas idénticas de la misma clínica y el mismo rol, una
//  con la marca de plataforma y CERO secciones: la API les contesta EL MISMO 403 en
//  `/api/administracion/resumen`, `/api/admin/dashboard`, `/api/contabilidad/config`,
//  `/api/derivaciones/:id/anonimizar`, `/api/encuestas/cuestionario`,
//  `/api/conversaciones/errores-envio/:id/marcar-visto`, `/api/anestesistas/:id/preferencias` y
//  `/api/agentes/:id?definitivo=1` — y aquellas doce líneas le pintaban «Panel de administración»,
//  la configuración contable, el botón de retirar datos personales y el rojo «Borrar
//  definitivamente». Un botón que promete lo que el servidor niega es peor que no tenerlo.
//
//  LA REGLA ES LA MISMA QUE EN EL SERVIDOR: NO COPIES EL CRITERIO, PREGÚNTALO. La decisión la toma
//  `backend/services/permisos.decidirAdmin` —la única frase que dice qué es ser administradora de
//  una clínica, la misma con la que las rutas contestan 403— y viaja YA RESUELTA en
//  `GET /api/permisos/mias` como `_es_admin`, igual que `_accesos`. Aquí se LEE. Deducirlo del mapa
//  de permisos ("¿tiene todas las claves?") sería tener la misma decisión escrita en dos
//  repositorios, que es exactamente como se llegó a esto.
//
//  SE LEE CERRADO, y esa es la única forma correcta de leerlo: mientras la respuesta no ha llegado
//  —o si la petición falla— `_es_admin` no está y esto contesta que NO. O sea que en el primer
//  instante de una recarga completa el administrador no ve su botón y aparece medio segundo
//  después. Al revés (abrir mientras carga) el precio es enseñarle a la persona de soporte un
//  gesto irreversible que el servidor le va a negar, y ese es el fallo que se viene a cerrar.
//  Quien necesite distinguir "todavía no lo sé" de "no" tiene `yaLlegaron(perms)`.
//
//  QUÉ NO ES ESTO. No es la seguridad: la seguridad es el 403 del servidor, que sigue estando y no
//  depende de esto. Esto es que la pantalla no prometa lo que ese 403 va a negar.
import { useQuery } from '@tanstack/react-query'
import api from './api'
import { recordarNombresRol } from '../utils/nombreRol'

// LA RESPUESTA DEL SERVIDOR, LEÍDA. Es la única definición que hay en el navegador.
export function esAdminDeLaClinica(perms) {
  return perms?._es_admin === true
}

// ¿ES DE RECEPCIÓN? Sale del rol BASE, que es el que viaja siempre: un rol personalizado hereda de
// uno de serie, y lo que aquí importa es de cuál hereda, no cómo lo haya llamado la clínica.
//
// SE USA PARA ENSEÑAR, NUNCA PARA ABRIR NADA. Igual que `esAdminDeLaClinica`: la seguridad es el 403
// del servidor. Esto solo decide si una pantalla ofrece algo — hoy, el enlace del formulario para
// que otros profesionales deriven sin cuenta (B-715): recepción es quien atiende al profesional que
// llama para derivar a alguien, así que es justo quien necesita ese enlace a mano.
export function esRecepcionDeLaClinica(perms) {
  return String(perms?._base_rol || '').toLowerCase() === 'recepcion'
}

// ¿Ha llegado ya la respuesta? `_base_rol` viene SIEMPRE en el cuerpo de /permisos/mias, así que
// sirve de "ya se sabe". Sin esto no se puede distinguir "todavía no" de "no", y esa confusión es la
// que expulsa de su propia pantalla a quien acaba de pulsar F5.
export function yaLlegaron(perms) {
  return perms?._base_rol !== undefined
}

// ── LA CONSULTA, ESCRITA UNA VEZ ─────────────────────────────────────────────────────────────
//  La comparten `hooks/usePermisos` (el usuario EFECTIVO, con la previsualización "ver como" si la
//  hay) y `context/AuthContext` (el usuario REAL, que es quien puede entrar y salir de esa
//  previsualización). Escrita en los dos sitios, un día pedirían rutas distintas o cachearían con
//  claves distintas y las dos mitades de la aplicación decidirían con respuestas diferentes.
//
//  LA CLAVE DE CACHÉ LLEVA EL "VER COMO" DENTRO a propósito: sin previsualizar, los dos usos caen en
//  la MISMA entrada (`['permisos-mias', null, null]`) y react-query hace UNA sola petición;
//  previsualizando son dos entradas distintas, que es justo lo que hace falta para que el banner de
//  "volver a administrador" siga saliendo.
export function consultaPermisos({ verComoUsuarioId = null, verComoRol = null, habilitada = true } = {}) {
  const q = verComoUsuarioId ? `?ver_como_usuario=${verComoUsuarioId}`
          : verComoRol ? `?ver_como=${encodeURIComponent(verComoRol)}` : ''
  return {
    queryKey: ['permisos-mias', verComoUsuarioId || null, verComoRol || null],
    // B-590 · con los permisos llegan los nombres de los roles de la clínica: se dejan en utils/nombreRol para que
    // ninguna pantalla pinte «r_doctor_a».
    queryFn: () => api.get('/permisos/mias' + q).then(r => { recordarNombresRol(r.data && r.data._nombres_roles); return r.data }).catch(() => ({})),
    staleTime: 60000,
    enabled: habilitada !== false,
  }
}

// ── «VER COMO…» ES UNA HERRAMIENTA DE PRUEBAS DEL EQUIPO DE BIANGO (B-1000, 1-oct-2026) ────────
//  El dueño: «El botón "Ver como" solo debe salir a los superadmin: es un botón de pruebas». Ser
//  administradora de la clínica ya no basta. La decisión la toma el servidor con la cuenta REAL
//  (`backend/services/verComo`, el mismo criterio con el que acepta o ignora la previsualización) y
//  viaja YA RESUELTA en `GET /api/permisos/mias` como `_puede_ver_como`. Aquí se LEE, igual que
//  `_es_admin`: no se deduce del `user` guardado en el navegador, que cualquiera puede editar y que
//  se queda viejo si a la cuenta le quitan la marca.
//
//  SE LEE CERRADO: mientras la respuesta no ha llegado, o si el servidor no manda el campo, es NO.
export function puedeVerComoOtro(perms) {
  return perms?._puede_ver_como === true
}

// El mismo par (consulta + lectura) para quien solo quiere la respuesta. `habilitada` existe para el
// AuthContext: en la pantalla de entrar no hay sesión, y pedir permisos ahí solo serviría para que
// el interceptor de `services/api` se encontrara un 401 en la propia pantalla de login.
//
// `puedeVerComo` (B-1000) sale de la MISMA respuesta: quien pregunta por la cuenta REAL (AuthContext,
// sin «ver como» en la consulta) se entera a la vez de si administra la clínica y de si es la cuenta
// de pruebas. `listo` le sirve además para distinguir «todavía no lo sé» de «no», que es lo que
// impide borrarle la previsualización a quien prueba mientras su respuesta está de camino tras un F5.
export function useEsAdminDeLaClinica({ verComoUsuarioId = null, verComoRol = null, habilitada = true } = {}) {
  const { data } = useQuery(consultaPermisos({ verComoUsuarioId, verComoRol, habilitada }))
  return { esAdmin: esAdminDeLaClinica(data), listo: yaLlegaron(data), perms: data || {}, puedeVerComo: puedeVerComoOtro(data) }
}

export default esAdminDeLaClinica
